import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  TrendingUp, TrendingDown, DollarSign, Calendar, BarChart3, 
  Plus, Trash2, RefreshCw, ChevronDown, ChevronUp, FileText,
  PiggyBank, Zap, Home, Users, Wrench, ShoppingCart, Wine, Pizza
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { getApiHeaders } from '../utils/apiHelpers';

const API = process.env.REACT_APP_BACKEND_URL;

// Icone per categorie
const categoriaIcons = {
  'Alimenti': Pizza,
  'Bevande': Wine,
  'Materie Prime': ShoppingCart,
  'Utenze': Zap,
  'Affitto': Home,
  'Personale': Users,
  'Manutenzione': Wrench,
  'Marketing': TrendingUp,
  'Altro': FileText,
  'Non categorizzato': FileText
};

const CostiRicaviSection = () => {
  const [loading, setLoading] = useState(true);
  const [costiPerCategoria, setCostiPerCategoria] = useState([]);
  const [ricaviMensili, setRicaviMensili] = useState([]);
  const [totaliMese, setTotaliMese] = useState({ costi: 0, ricavi: 0 });
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [compareMonth, setCompareMonth] = useState('');
  const [showAddRicavo, setShowAddRicavo] = useState(false);
  const [newRicavo, setNewRicavo] = useState({ mese: '', importo: 0, note: '' });
  const [expandedCategoria, setExpandedCategoria] = useState(null);
  const [dettagliFatture, setDettagliFatture] = useState([]);
  
  // Usa l'helper centralizzato per gli headers
  const getAuthHeaders = getApiHeaders;

  // Carica costi per categoria dal backend (fatture categorizzate)
  const loadCostiCategoria = useCallback(async () => {
    try {
      const [anno, mese] = selectedMonth.split('-');
      const res = await axios.get(`${API}/api/gestione/fatture/statistiche/categorie`, {
        params: { anno: parseInt(anno), mese: parseInt(mese) },
        headers: getAuthHeaders()
      });
      
      // Trasforma i dati per il frontend
      const categorie = (res.data.categorie || []).map(cat => ({
        categoria: cat.categoria,
        totale: cat.totale_importo || 0,
        count: cat.totale_fatture || 0
      }));
      
      setCostiPerCategoria(categorie);
      const totale = categorie.reduce((sum, c) => sum + c.totale, 0);
      setTotaliMese(prev => ({ ...prev, costi: totale }));
      
    } catch (error) {
      console.error('Errore caricamento costi fatture:', error);
      // Fallback: nessun costo
      setCostiPerCategoria([]);
      setTotaliMese(prev => ({ ...prev, costi: 0 }));
      try {
        const res = await axios.get(`${API}/api/gestione/tenant/spese?limit=500`, {
          headers: getAuthHeaders()
        });
        const spese = res.data.spese || [];
        
        // Filtra per mese selezionato
        const [anno, mese] = selectedMonth.split('-');
        const speseMese = spese.filter(s => {
          const dataSpesa = new Date(s.data);
          return dataSpesa.getFullYear() === parseInt(anno) && 
                 (dataSpesa.getMonth() + 1) === parseInt(mese);
        });
        
        // Raggruppa per categoria
        const grouped = speseMese.reduce((acc, s) => {
          const cat = s.categoria || 'Altro';
          if (!acc[cat]) acc[cat] = { categoria: cat, totale: 0, count: 0 };
          acc[cat].totale += s.importo || 0;
          acc[cat].count++;
          return acc;
        }, {});
        
        setCostiPerCategoria(Object.values(grouped));
        setTotaliMese(prev => ({ 
          ...prev, 
          costi: speseMese.reduce((sum, s) => sum + (s.importo || 0), 0) 
        }));
      } catch (e) {
        
      }
    }
  }, [selectedMonth]);

  // Carica ricavi mensili
  const loadRicavi = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/gestione/tenant/ricavi`, {
        headers: getAuthHeaders()
      });
      setRicaviMensili(res.data.ricavi || []);
      
      // Trova ricavo del mese selezionato
      const ricavoMese = (res.data.ricavi || []).find(r => r.mese === selectedMonth);
      setTotaliMese(prev => ({ ...prev, ricavi: ricavoMese?.importo || 0 }));
    } catch (error) {
      console.error('Errore caricamento ricavi:', error);
    }
  }, [selectedMonth]);

  // Carica dettagli fatture per categoria
  const loadDettagliFatture = async (categoria) => {
    try {
      const [anno, mese] = selectedMonth.split('-');
      const res = await axios.get(`${API}/api/gestione/fatture`, {
        params: { 
          anno: parseInt(anno), 
          mese: parseInt(mese),
          categoria: categoria,
          limit: 50
        },
        headers: getAuthHeaders()
      });
      setDettagliFatture(res.data.fatture || []);
    } catch (error) {
      console.error('Errore caricamento dettagli:', error);
      setDettagliFatture([]);
    }
  };

  // Aggiungi ricavo mensile
  const handleAddRicavo = async () => {
    if (!newRicavo.mese || !newRicavo.importo) {
      toast.error('Inserisci mese e importo');
      return;
    }
    try {
      await axios.post(`${API}/api/gestione/tenant/ricavi`, newRicavo, {
        headers: getAuthHeaders()
      });
      toast.success('Fatturato registrato!');
      setShowAddRicavo(false);
      setNewRicavo({ mese: '', importo: 0, note: '' });
      loadRicavi();
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  // Elimina ricavo
  const handleDeleteRicavo = async (id) => {
    if (!window.confirm('Eliminare questo fatturato?')) return;
    try {
      await axios.delete(`${API}/api/gestione/tenant/ricavi/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Fatturato eliminato');
      loadRicavi();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadCostiCategoria(), loadRicavi()]);
      setLoading(false);
    };
    loadAll();
  }, [loadCostiCategoria, loadRicavi]);

  // Toggle espansione categoria
  const toggleCategoria = (categoria) => {
    if (expandedCategoria === categoria) {
      setExpandedCategoria(null);
      setDettagliFatture([]);
    } else {
      setExpandedCategoria(categoria);
      loadDettagliFatture(categoria);
    }
  };

  // Calcola margine
  const margine = totaliMese.ricavi - totaliMese.costi;
  const marginePerc = totaliMese.ricavi > 0 ? ((margine / totaliMese.ricavi) * 100).toFixed(1) : 0;

  // Dati per confronto storico
  const getConfrontoData = () => {
    if (!compareMonth) return null;
    const ricavoCompare = ricaviMensili.find(r => r.mese === compareMonth);
    // Per i costi del mese di confronto, dovremmo fare una chiamata API separata
    // Per ora mostriamo solo i ricavi
    return {
      mese: compareMonth,
      ricavi: ricavoCompare?.importo || 0
    };
  };

  const confronto = getConfrontoData();

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Gestione Costi/Ricavi</h1>
          <p className="text-gray-500 text-sm">Analisi finanziaria del ristorante</p>
        </div>
        <div className="flex gap-2">
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
          />
          <button 
            onClick={() => {
              setNewRicavo({ mese: selectedMonth, importo: 0, note: '' });
              setShowAddRicavo(true);
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Registra Fatturato</span>
          </button>
        </div>
      </div>

      {/* Cards Riepilogo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Costi */}
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-sm">Costi Totali</p>
                <p className="text-3xl font-bold">€{totaliMese.costi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
              <TrendingDown className="h-10 w-10 text-red-200" />
            </div>
            <p className="text-red-200 text-xs mt-2">{costiPerCategoria.length} categorie</p>
          </CardContent>
        </Card>

        {/* Ricavi */}
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Fatturato</p>
                <p className="text-3xl font-bold">€{totaliMese.ricavi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-200" />
            </div>
            <p className="text-green-200 text-xs mt-2">Mese: {selectedMonth}</p>
          </CardContent>
        </Card>

        {/* Margine */}
        <Card className={`bg-gradient-to-br ${margine >= 0 ? 'from-blue-500 to-blue-600' : 'from-orange-500 to-orange-600'} text-white`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`${margine >= 0 ? 'text-blue-100' : 'text-orange-100'} text-sm`}>Margine</p>
                <p className="text-3xl font-bold">€{margine.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
              </div>
              <PiggyBank className={`h-10 w-10 ${margine >= 0 ? 'text-blue-200' : 'text-orange-200'}`} />
            </div>
            <p className={`${margine >= 0 ? 'text-blue-200' : 'text-orange-200'} text-xs mt-2`}>
              {marginePerc}% del fatturato
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Grafico Costi vs Ricavi - Barre Verticali */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-cyan-500" />
            Costi vs Ricavi - {selectedMonth}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-center gap-16 h-64 pt-4">
            {/* Colonna Costi */}
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-red-600 mb-2">
                €{totaliMese.costi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
              <div className="relative w-24 bg-gray-100 rounded-t-lg overflow-hidden" style={{ height: '180px' }}>
                <div 
                  className="absolute bottom-0 w-full bg-gradient-to-t from-red-600 to-red-400 rounded-t-lg transition-all duration-700 ease-out"
                  style={{ 
                    height: `${Math.max(totaliMese.costi, totaliMese.ricavi) > 0 
                      ? (totaliMese.costi / Math.max(totaliMese.costi, totaliMese.ricavi)) * 100 
                      : 0}%` 
                  }}
                />
              </div>
              <span className="mt-3 text-sm font-medium text-gray-700">Costi</span>
            </div>
            
            {/* Colonna Ricavi */}
            <div className="flex flex-col items-center">
              <span className="text-lg font-bold text-green-600 mb-2">
                €{totaliMese.ricavi.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
              </span>
              <div className="relative w-24 bg-gray-100 rounded-t-lg overflow-hidden" style={{ height: '180px' }}>
                <div 
                  className="absolute bottom-0 w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all duration-700 ease-out"
                  style={{ 
                    height: `${Math.max(totaliMese.costi, totaliMese.ricavi) > 0 
                      ? (totaliMese.ricavi / Math.max(totaliMese.costi, totaliMese.ricavi)) * 100 
                      : 0}%` 
                  }}
                />
              </div>
              <span className="mt-3 text-sm font-medium text-gray-700">Ricavi</span>
            </div>
          </div>
          
          {/* Linea di riferimento e legenda */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded"></div>
                <span className="text-gray-600">Costi del mese</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-gray-600">Ricavi del mese</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confronto Storico */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            Confronto Storico
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Confronta con:</span>
              <input
                type="month"
                value={compareMonth}
                onChange={(e) => setCompareMonth(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          </div>
          
          {confronto && (
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-cyan-50 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">{selectedMonth}</p>
                <p className="text-xl font-bold text-cyan-700">€{totaliMese.ricavi.toLocaleString('it-IT')}</p>
                <p className="text-xs text-gray-500">Fatturato</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-gray-600 mb-1">{compareMonth}</p>
                <p className="text-xl font-bold text-purple-700">€{confronto.ricavi.toLocaleString('it-IT')}</p>
                <p className="text-xs text-gray-500">Fatturato</p>
              </div>
              {confronto.ricavi > 0 && (
                <div className="col-span-2 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-center">
                    {totaliMese.ricavi > confronto.ricavi ? (
                      <span className="text-green-600 font-medium">
                        +€{(totaliMese.ricavi - confronto.ricavi).toLocaleString('it-IT')} ({((totaliMese.ricavi - confronto.ricavi) / confronto.ricavi * 100).toFixed(1)}%) rispetto a {compareMonth}
                      </span>
                    ) : totaliMese.ricavi < confronto.ricavi ? (
                      <span className="text-red-600 font-medium">
                        -€{(confronto.ricavi - totaliMese.ricavi).toLocaleString('it-IT')} ({((confronto.ricavi - totaliMese.ricavi) / confronto.ricavi * 100).toFixed(1)}%) rispetto a {compareMonth}
                      </span>
                    ) : (
                      <span className="text-gray-600">Stesso fatturato di {compareMonth}</span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {!compareMonth && (
            <p className="text-center text-gray-400 text-sm py-4">
              Seleziona un mese per confrontare i dati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dettaglio Costi per Categoria */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-red-500" />
            Costi per Categoria
          </CardTitle>
        </CardHeader>
        <CardContent>
          {costiPerCategoria.length > 0 ? (
            <div className="space-y-2">
              {costiPerCategoria.map((cat, idx) => {
                const IconComponent = categoriaIcons[cat.categoria] || FileText;
                const percentuale = totaliMese.costi > 0 ? ((cat.totale / totaliMese.costi) * 100).toFixed(1) : 0;
                const isExpanded = expandedCategoria === cat.categoria;
                
                return (
                  <div key={idx} className="border border-gray-100 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleCategoria(cat.categoria)}
                      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <IconComponent className="h-5 w-5 text-red-600" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{cat.categoria}</p>
                          <p className="text-xs text-gray-500">{cat.count || 0} voci</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-gray-900">€{(cat.totale || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                          <p className="text-xs text-gray-500">{percentuale}%</p>
                        </div>
                        {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                      </div>
                    </button>
                    
                    {/* Barra percentuale */}
                    <div className="px-4 pb-2">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-red-400 to-red-500 rounded-full transition-all duration-300"
                          style={{ width: `${percentuale}%` }}
                        />
                      </div>
                    </div>
                    
                    {/* Dettagli espansi */}
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                        <p className="text-xs text-gray-500 py-2">Dettaglio fatture:</p>
                        {dettagliFatture.length > 0 ? (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {dettagliFatture.map((ft, i) => (
                              <div key={i} className="flex justify-between text-sm p-2 bg-white rounded">
                                <span className="text-gray-700 truncate flex-1">{ft.fornitore || 'N/D'}</span>
                                <span className="text-gray-500 mx-2">{ft.data || ''}</span>
                                <span className="font-medium text-red-600">€{(ft.importo || 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 text-center py-2">Caricamento...</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">
              Nessun costo registrato per questo mese
            </p>
          )}
        </CardContent>
      </Card>

      {/* Storico Fatturato */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Storico Fatturato
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ricaviMensili.length > 0 ? (
            <div className="space-y-2">
              {ricaviMensili.slice().reverse().map((ricavo, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{ricavo.mese}</p>
                      {ricavo.note && <p className="text-xs text-gray-500">{ricavo.note}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-green-600">€{(ricavo.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}</p>
                    <button 
                      onClick={() => handleDeleteRicavo(ricavo.id || ricavo._id)}
                      className="p-1.5 hover:bg-red-100 rounded-lg text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">
              Nessun fatturato registrato. Clicca "Registra Fatturato" per iniziare.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modal Aggiungi Ricavo */}
      {showAddRicavo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Registra Fatturato Mensile</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mese</label>
                <input
                  type="month"
                  value={newRicavo.mese}
                  onChange={(e) => setNewRicavo({...newRicavo, mese: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Importo (€)</label>
                <input
                  type="number"
                  value={newRicavo.importo}
                  onChange={(e) => setNewRicavo({...newRicavo, importo: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Es: 15000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note (opzionale)</label>
                <input
                  type="text"
                  value={newRicavo.note}
                  onChange={(e) => setNewRicavo({...newRicavo, note: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  placeholder="Es: Mese record, eventi speciali..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => setShowAddRicavo(false)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Annulla
              </button>
              <button 
                onClick={handleAddRicavo}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostiRicaviSection;
